import { useEffect, useState } from "react"
import Router, { useRouter } from "next/router"
import { useCookies } from "react-cookie"
import Zendesk from "react-zendesk"
import { useApolloClient } from "@apollo/react-hooks"

import { ProvideVideoPlayer } from "lib/video-player"
import { useLanguages } from "lib/languages"
import { useAuth } from "lib/auth"
import { useModal } from "lib/modal"
import { useCountry } from "lib/country"
import { i18n } from "lib/i18n"
import isBrowser from "lib/tools/is-browser"
import { useAccesses } from "lib/accesses"
import { isSubscriptionUrl } from "lib/subscription/is-subscription-url"
import { FETCH_PREHOME } from "lib/graphql"
import { start, stop } from "lib/tools/timer"

import Prehome from "components/prehome"
import { Foreigner } from "components/modals/foreigner"
import { ForeignSubscription } from "components/modals/foreign-subscription"
import { HeaderAccount } from "components/profile"
import { Maintenance } from "components/modals/maintenance"

import { GtmInit, Header, HeaderAdmin, Footer, Modal, Toast } from "./"
import { AccessInfo } from "./header/access-info"

let lastScroll = 0

const Layout = ({ children, inMaintenance }) => {
	const { show } = useModal()
	const { hasSubscribed } = useAccesses()
	const { user } = useAuth()
	const [hasStickyClass, setHasStickyClass] = useState(true)
	const {
		isCountryLoaded,
		inEuro,
		country,
		originCountry,
		userCountry,
		available,
		purchasable,
		originalCountry,
		originalAccess: userCountryMatching,
		countries,
		accesses: {
			access,
			originalAccess
		}
	} = useCountry()
	const router = useRouter()
	const { current } = useLanguages()
	const [myAccount, setMyAccount] = useState(false)
	const [prehome, setPrehome] = useState(null)
	const apolloClient = useApolloClient()

	const [cookies] = useCookies(["access",
		"original-access",
		"country",
		"next-i18next",
		"prehome",
		"prehomeSession",
		"prehomeEvent",
		"prehomeEventId"
	])

	const getPrehome = async () => {
		const prehome = await apolloClient.query({ query: FETCH_PREHOME, context: {
			clientName: "admin",
			headers: {
				"X-Prehome": Number(cookies.prehome || 0),
				"X-Prehome-Event": Number(cookies.prehomeEvent || 0),
				"X-Prehome-Event-Id": Number(cookies.prehomeEventId || 0),
				"X-Prehome-Session": Number(cookies.prehomeSession || 0),
				"X-Country": country ? country.isoCode : "fr",
				"Accept-Language": current
			}
		} })
		return prehome.data.prehome
	}

	useEffect(() => {
		start("prehome")
		getPrehome()
			.then(data => {
				setPrehome(data)
				stop("prehome")
			})
	}, [])


	const updateViewPort = () => {
		const viewportWidth = document.querySelector("main").offsetWidth
		document.documentElement.style.setProperty("--viewport-width", `${viewportWidth}px`)
	}

	const handleScroll = () => {
		var body = document.body
		if (window.scrollY <= 50) {
			body.classList.add("top")
			body.classList.remove("scrolled")
		} else {
			body.classList.add("scrolled")
			body.classList.remove("top")
		}

		if (window.scrollY > 50) {
			if (window.scrollY < lastScroll) {
				body.classList.add("up")
				body.classList.remove("down")
			} else {
				body.classList.add("down")
				body.classList.remove("up")
			}
		} else {
			body.classList.remove("up")
			body.classList.remove("down")
		}
		lastScroll = window.scrollY
	}

	const manageSvodClass = () => {
		if (
			user
			&& hasSubscribed
			&& isSubscriptionUrl(Router)
		) {
			document.body.classList.add("subscribed")
		} else {
			document.body.classList.remove("subscribed")
		}
	}

	const debugForeignModal = (message) => {
		console.log(message)
		try {
			console.log("cookies access",cookies.access);
			console.log("cookies original-access",cookies["original-access"]);
			console.log("cookies country",cookies.country);
			console.log("cookies next-i18next",cookies["next-i18next"]);

			console.log("originalCountry", originalCountry)
			console.log("User", user)
			console.log("originCountry.isoCode", originCountry ? originCountry.isoCode : "undefined")
			console.log("country.isoCode", country ? country.isoCode : "undefined")
			console.log("userCountry.isoCode", userCountry ? userCountry.isoCode : "undefined")
			console.log("inEuro", inEuro)
			console.log("hasSubscribed", hasSubscribed)
			console.log("access", access)
			console.log("available", available)
			console.log("purchasable", purchasable)
		} catch(error) {
			console.log("Something went wrong in debug")
			console.log(error)
		}
	}

	useEffect(() => {
		if (inMaintenance) {
			show({
				id: "mainteance-modal",
				children: <Maintenance />,
				className: "min red bottom interactive",
				contentClassName: "centered",
				role: "alertdialog"
			})
		}

		manageSvodClass()
		window.addEventListener('scroll', handleScroll)
		Router.events.on("routeChangeComplete", manageSvodClass)
		updateViewPort()

		return () => {
			window.removeEventListener('scroll', handleScroll)
			Router.events.off("routeChangeComplete", manageSvodClass)
		}
	}, [])

	useEffect(() => {
		if (!isCountryLoaded) {
			return
		}

		debugForeignModal("------LOAD LAYOUT------")

		if (
			!userCountryMatching
			&& (countries.find(item => item.isoCode == access.country.toLowerCase())
				|| countries.find(item => item.isoCode == originalAccess.country.toLowerCase()))
		) {
			showPlatformCountryCurrentlyDisplay()
		}

		if (user && inEuro && !originalCountry && hasSubscribed) {
			showUserHasForeignSubscription()
		} else if (!purchasable) {
			showPlatformUnavailableInUserCountry()
		}
	}, [isCountryLoaded, user, originalCountry])

	useEffect(() => {
		manageSvodClass()
	}, [user])

	useEffect(() => {
		document.body.classList.remove("fr", "en", "de")
		document.body.classList.add(current)

		const htmlLang = i18n.language
			? i18n.language.toLowerCase()
				+ (
					country.isoCode
					? "-" + country.isoCode.toUpperCase()
					: ""
				)
			: "fr"

		document.documentElement.setAttribute('lang', htmlLang)
		i18n.on('languageChanged', (lng) => {
			document.documentElement.setAttribute('lang', htmlLang)
		})
	}, [current])

	useEffect(() => {
		setHasStickyClass(!!router.asPath.match(/cart/))
		setMyAccount(document.body.className.includes("private") && !router.asPath.match(/administration/))
	}, [router])

	const showPlatformUnavailableInUserCountry = () => {
		if ((router && router.query["reset-password-for"]) || (inEuro && originCountry && country.isoCode == originCountry.isoCode)) return

		show({
			id: "restriction-modal",
			children: <Foreigner />,
			className: "min red top interactive",
			contentClassName: "centered",
			role: "alertdialog"
		})
	}

	const showUserHasForeignSubscription = () => {
		if (!router || !router.query["reset-password-for"]) {
			show({
				id: "foreign-subscription-modal",
				children: <ForeignSubscription />,
				className: "min red top interactive",
				contentClassName: "centered",
				role: "alertdialog"
			})
		}
	}

	const showPlatformCountryCurrentlyDisplay = () => {
		if (!router || !router.query["reset-password-for"]) {
			show({
				id: "foreign-access-modal",
				children: <AccessInfo />,
				className: "min red top interactive",
				contentClassName: "centered",
				role: "alertdialog"
			})
		}
	}

	const ZENDESK_KEY = "web_widget/lacinetekhelp.zendesk.com"
	const zendeskSettings = {
		color: {
			theme: "#d92e41"
		},
		helpCenter: {
			title: "Aide",
			searchPlaceholder: "Comment pouvons-nous vous aider ?"
		},
		webWidget: {
			zIndex: 5000
		}
	}

	const backOffice = isBrowser ? router.asPath.includes("administration") : false

	return (
		<div id="wrapper">
			<ProvideVideoPlayer>
				<GtmInit />
				{
					backOffice
						? <HeaderAdmin />
						: <Header />
				}
				{
					myAccount
					&& <HeaderAccount />
				}
				<main className={`${hasStickyClass ? "has-sticky" : ""}`}>
					{children}
				</main >
				<Footer backOffice={backOffice} />
				<Modal />
				<Toast />
				<Zendesk zendeskKey={ZENDESK_KEY} {...zendeskSettings} onLoaded={() => {
					if (prehome) {
						zE("messenger", "hide")
					}
				}} />
				<Prehome prehome={prehome} />
			</ProvideVideoPlayer>
		</div>
	)
}

export default Layout
